// Workflow detail (design/isomorphic workflow-detail): prompt and guard, the trigger, the
// tool policy, recent runs and the last 30 days, with "Run now".
import { Play } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import { Button } from "rizzui";

import { environmentsQuery, workspacesQuery } from "../../api/queries";
import { useT } from "../../i18n";
import { PageHeader } from "../../ui/PageHeader";
import { ErrorState, Loading } from "../../ui/states";
import { workflowQuery, workflowRunsQuery } from "../api";
import { cliName } from "../format";
import { newestFirst, workflowStats } from "../runs";
import { useTriggerRun } from "../useTriggerRun";
import { EnabledMark } from "./WorkflowBadges";
import { PromptCard, RecentRunsCard, ThirtyDaysCard, ToolPolicyCard } from "./WorkflowCards";
import { detailCrumbs } from "../../layout/nav";

export default function WorkflowDetailPage() {
  const { workloads } = useT();
  const { workflowId = "" } = useParams();
  const workflow = useQuery(workflowQuery(workflowId));
  const runs = useQuery(workflowRunsQuery(workflowId));
  const environments = useQuery(environmentsQuery());
  const workspaces = useQuery(workspacesQuery());
  const trigger = useTriggerRun();

  if (workflow.isPending) return <Loading />;
  if (workflow.isError) return <ErrorState title={workloads.workflow.couldNotLoad} message={workflow.error.message} />;
  const w = workflow.data;
  const recent = newestFirst(runs.data ?? []);
  const where = [environments.data?.find((e) => e.id === w.environment_id)?.name, workspaces.data?.find((x) => x.id === w.workspace_id)?.name];

  return (
    <>
      <PageHeader
        title={w.name}
        breadcrumb={detailCrumbs("legacyWorkflows", "/legacy-workflows", { name: w.name })}
        description={[...where, cliName(w.cli_provider), w.model].filter(Boolean).join(" · ")}
      >
        <EnabledMark enabled={w.enabled} />
        <Button className="gap-1.5" disabled={!w.enabled || trigger.isPending} onClick={() => trigger.mutate(w.id)}>
          <Play weight="fill" className="h-4 w-4" /> {workloads.legacy.runNow}
        </Button>
      </PageHeader>
      <div className="grid gap-5 xl:grid-cols-12">
        <div className="min-w-0 space-y-5 xl:col-span-7">
          <PromptCard workflow={w} />
          {runs.isError ? <ErrorState message={runs.error.message} /> : <RecentRunsCard runs={recent} />}
        </div>
        <div className="min-w-0 space-y-5 xl:col-span-5">
          <ToolPolicyCard workflow={w} />
          <ThirtyDaysCard stats={workflowStats(recent)} workflow={w} />
        </div>
      </div>
    </>
  );
}
